import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { Notification } from "../../@types/Notification";
import { IWorkflowsTableContent } from "../../@types/Worflows";
import { CircleChecked } from "../../assets/icons/CircleChecked";
import { CircleUnchecked } from "../../assets/icons/CircleUncheked";
import { Dots } from "../../assets/icons/Dots";
import { CustomIconButton } from "../../components/CustomIconButton";
import { Dropdown, DropdownTextItem } from "../../components/Dropdown";
import { IOption, SelectDropdown } from "../../components/SelectDropdown";
import { ActionContainer } from "../../components/TableActionDropdownButton/TableActionDropdownButton.styles";
import {
  AUTH_USER_DETAILS,
  NOTIFICATION,
} from "../../constants/localStorageKeys";
import { useWorkflows } from "../../hooks/queries/workflows";
import { useModal } from "../../hooks/useModal";
import { useToast } from "../../hooks/useToast";
import { PageLayout } from "../../layout/components/PageLayout";
import { PageTitle } from "../../layout/components/PageTitle";
import { hospitalService } from "../../services/hospitalService";
import { getLongFormattedDate } from "../../utils/dateFormatter";
import { mixpanelActions } from "../../utils/mixpanel";
import { safeStorage, safeJsonParse } from "../../utils/storage";
import { DuplicateWorkflowModal } from "./components/DuplicateWorkflowModal";
import { WorflowListTable } from "./components/WorflowListTable";
import { WorkflowStatusChangeModal } from "./components/WorkflowsBuilder/components/WorkflowStatusChangeModal";
import {
  AddWorkflowButton,
  WorkflowListActions,
  HospitalGroupSelectCss,
  PageTitleContainer,
  PMPHospitalGroupSelect,
  WorflowsTableContainer,
  WorkflowListContainer,
} from "./PatientWorkflows.styles";

export function WorkflowsList() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();

  const [isPMPUser, setIsPMPUser] = useState(false);
  const [hospitalGroupOptions, setHospitalGroupOptions] = useState<IOption[]>(
    []
  );
  const [selectedHospitalGroup, setSelectedHospitalGroup] = useState<
    string | undefined
  >(undefined);

  const { workflows, isFetching, isRefetching, refetch } = useWorkflows(
    Number.parseInt(selectedHospitalGroup ?? "", 10)
  );

  const parsedArticles = useMemo(() => {
    if (!workflows || workflows.length === 0) return [];

    return workflows?.map((workflow: IWorkflowsTableContent) => {
      // EN-5191: ids, not labels — `published={val === options[1]}` below matches on this.
      const options = ["unpublish", "publish"];

      const handleOnModalClose = (
        notification?: Notification,
        refetchTable?: boolean
      ) => {
        if (notification) {
          showToast(notification.message, notification.type);
        }

        if (refetchTable) {
          refetch();
        }

        closeModal();
      };

      return {
        id: workflow.id,
        title: workflow.title,
        description: workflow.description,
        updated:
          workflow.updated !== undefined
            ? getLongFormattedDate(workflow.updated)
            : getLongFormattedDate(workflow.created),
        published: (
          <div style={{ marginLeft: "30px" }}>
            {workflow.published ? <CircleChecked /> : <CircleUnchecked />}
          </div>
        ),
        action: (
          <div>
            <ActionContainer
              className="actionContainer"
              onClick={(e) => e.stopPropagation()}
            >
              <Dropdown
                trigger={
                  <CustomIconButton>
                    <Dots />
                  </CustomIconButton>
                }
                value=""
                onValueChange={(val: string) => {
                  if (val === "duplicate") {
                    openModal(
                      <DuplicateWorkflowModal
                        workflowId={workflow.id}
                        onClose={handleOnModalClose}
                      />,
                      {
                        width: "550px",
                        height: "100%",
                        showCloseButton: true,
                      }
                    );
                    return;
                  }

                  openModal(
                    <WorkflowStatusChangeModal
                      workflowId={workflow.id}
                      published={val === options[1]}
                      isWorkflowList
                      onClose={handleOnModalClose}
                    />,
                    {
                      width: "623px",
                      height: "100%",
                      showCloseButton: true,
                    }
                  );
                }}
              >
                {workflow.published ? (
                  <DropdownTextItem
                    key="unpublish"
                    value="unpublish"
                    text={t("workflows_actions_unpublish")}
                  />
                ) : (
                  <DropdownTextItem
                    key="publish"
                    value="publish"
                    text={t("workflows_actions_publish")}
                  />
                )}
                {isPMPUser ? (
                  <DropdownTextItem
                    key="duplicate"
                    value="duplicate"
                    text={t("workflows_actions_duplicate")}
                  />
                ) : null}
              </Dropdown>
            </ActionContainer>
          </div>
        ),
      } as IWorkflowsTableContent;
    });
  }, [workflows, isPMPUser, t]);

  const handleOnRowClick = (rowContent: IWorkflowsTableContent) => {
    navigate(
      `/workflows/builder/${rowContent.id}${
        selectedHospitalGroup ? `?hospitalGroupId=${selectedHospitalGroup}` : ""
      }`
    );
  };

  const handleAddWorkflowClick = () => {
    navigate(
      `/workflows/builder${
        selectedHospitalGroup ? `?hospitalGroupId=${selectedHospitalGroup}` : ""
      }`
    );
  };

  useEffect(() => {
    mixpanelActions.track("PageView: Care Info");
  }, []);

  useEffect(() => {
    mixpanelActions.track("PageView: Workflows");

    const savedNotification = safeStorage.getItem(NOTIFICATION);

    if (savedNotification) {
      const parsedNotification = safeJsonParse<Notification>(
        savedNotification,
        {} as Notification
      );

      showToast(parsedNotification.message, parsedNotification.type);

      safeStorage.removeItem(NOTIFICATION);
    }

    refetch();

    const localStorageUserDetails =
      safeStorage.getItem(AUTH_USER_DETAILS) ?? "";
    const parsedUserDetails = safeJsonParse<any>(localStorageUserDetails, {});

    const userEmail = parsedUserDetails?.userEmail as string;

    const isPMPUser = userEmail.includes("patientmpower");

    setIsPMPUser(isPMPUser);

    if (isPMPUser) {
      hospitalService.getHospitalGroups().then((hospitalGroups) => {
        if (hospitalGroups.data === null) return;

        const groupsToAdd: IOption[] = [];

        hospitalGroups.data.forEach((group) => {
          groupsToAdd.push({
            label: group.hospitalGroupName,
            value: group.hospitalGroupId.toString(),
            key: uuidv4(),
          });
        });

        setHospitalGroupOptions(groupsToAdd);
      });
    }
  }, []);

  return (
    <PageLayout>
      <WorkflowListContainer>
        <PageTitleContainer>
          <PageTitle>{t("workflows_title")}</PageTitle>
        </PageTitleContainer>

        <WorflowsTableContainer>
          <WorkflowListActions>
            {isPMPUser ? (
              <PMPHospitalGroupSelect>
                <SelectDropdown
                  options={hospitalGroupOptions ?? []}
                  height={30}
                  placeholder={t("workflows_selectHospital")}
                  optionFilterProp="label"
                  value={selectedHospitalGroup}
                  onValueChange={(value: string | string[]) => {
                    setSelectedHospitalGroup(value.toString());
                  }}
                  className={HospitalGroupSelectCss()}
                />
              </PMPHospitalGroupSelect>
            ) : null}
            <AddWorkflowButton onClick={handleAddWorkflowClick}>
              {t("workflows_add")}
            </AddWorkflowButton>
          </WorkflowListActions>
          <WorflowListTable
            content={parsedArticles}
            isLoading={isFetching || isRefetching}
            onRowClick={handleOnRowClick}
          />
        </WorflowsTableContainer>
      </WorkflowListContainer>
    </PageLayout>
  );
}
